import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { 
  Mail, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw, 
  ExternalLink,
  Users,
  Play,
  BookOpen
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { resendConfirmationEmail, canResendConfirmationEmail, recordResendAttempt } from '@/services/auth/emailResendService';

interface EmailConfirmationWaitingProps {
  email: string;
  hasNewsletterConsent: boolean;
}

export function EmailConfirmationWaiting({ email, hasNewsletterConsent }: EmailConfirmationWaitingProps) {
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Timer for elapsed time and resend eligibility check
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);

    // Check if user can resend immediately
    canResendConfirmationEmail(email).then(setCanResend);

    // Allow resend after 2 minutes if not already allowed
    const resendTimer = setTimeout(() => {
      setCanResend(true);
    }, 120000); // 2 minutes

    return () => {
      clearInterval(timer);
      clearTimeout(resendTimer);
    };
  }, [email]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResendEmail = async () => {
    if (!canResend || isResending) return;

    setIsResending(true);
    try {
      const result = await resendConfirmationEmail(email);

      if (result.success) {
        recordResendAttempt(email);
        setResendCount(prev => prev + 1);
        setResendSuccess(true);
        setCanResend(false);

        // Reset resend availability after the specified time or 5 minutes
        const resetTime = result.rateLimited ? (result.retryAfter || 300) * 1000 : 300000;
        setTimeout(() => {
          setCanResend(true);
          setResendSuccess(false);
        }, resetTime);
      } else {
        // Handle error - could show error message to user
        console.error('Resend failed:', result.error);
        if (result.rateLimited) {
          setCanResend(false);
          const retryTime = (result.retryAfter || 300) * 1000;
          setTimeout(() => setCanResend(true), retryTime);
        }
      }
    } catch (error) {
      console.error('Failed to resend email:', error);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="mt-8 space-y-6">
      {/* Main Success Message */}
      <div className="text-center space-y-4">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        
        <div className="space-y-2">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">You're almost there!</h2>
          <p className="text-gray-600 break-words">
            We've sent a confirmation email to <strong className="text-gray-900">{email}</strong>
          </p>
          <p className="text-sm text-gray-500">
            Click the link in the email to complete your account setup{hasNewsletterConsent ? ' and newsletter subscription' : ''}.
          </p>
        </div>
      </div>

      {/* Status and Timer */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-3">
              <Mail className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-900">Email sent successfully</p>
                <p className="text-sm text-blue-700">Usually arrives within 2-3 minutes</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 text-blue-600 self-start sm:self-center">
              <Clock className="h-4 w-4" />
              <span className="font-mono text-sm">{formatTime(timeElapsed)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* What to Look For */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg flex items-center space-x-2">
            <Mail className="h-5 w-5 flex-shrink-0" />
            <span>What to look for</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="break-words">
              <span className="font-medium">From:</span> The Mat Monster &lt;no-reply@thematmonster.com&gt;
            </div>
            <div className="break-words">
              <span className="font-medium">Subject:</span> Confirm your Mat Monster account
            </div>
            <div className="break-words">
              <span className="font-medium">Action:</span> Click the blue "Confirm Account" button
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Troubleshooting */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base sm:text-lg flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />
            <span>Didn't receive the email?</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3 text-sm">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium">1</span>
              </div>
              <div>
                <p className="font-medium">Check your spam/junk folder</p>
                <p className="text-gray-600">Sometimes emails end up there, especially from new senders</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium">2</span>
              </div>
              <div>
                <p className="font-medium">Wait a few more minutes</p>
                <p className="text-gray-600">Email delivery can sometimes take up to 10 minutes</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-medium">3</span>
              </div>
              <div>
                <p className="font-medium">Add us to your contacts</p>
                <p className="text-gray-600">Add no-reply@thematmonster.com to ensure future emails arrive</p>
              </div>
            </div>
          </div>

          {/* Resend Button */}
          <div className="pt-4 border-t">
            {resendSuccess ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Email resent successfully! Check your inbox again.
                </AlertDescription>
              </Alert>
            ) : (
              <Button
                onClick={handleResendEmail}
                disabled={!canResend || isResending}
                variant="outline"
                className="w-full"
              >
                {isResending ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Resending...
                  </>
                ) : canResend ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Resend confirmation email
                  </>
                ) : (
                  <>
                    <Clock className="mr-2 h-4 w-4" />
                    Resend available in {Math.ceil((120 - timeElapsed) / 60)} min
                  </>
                )}
              </Button>
            )}
            {resendCount > 0 && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                Email resent {resendCount} time{resendCount > 1 ? 's' : ''}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* What You'll Get Access To */}
      <Card className="border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg text-green-900">What's waiting for you</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center space-x-3">
              <BookOpen className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-green-800">1000+ BJJ techniques with detailed breakdowns</span>
            </div>
            <div className="flex items-center space-x-3">
              <Play className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-green-800">Video tutorials from world-class instructors</span>
            </div>
            <div className="flex items-center space-x-3">
              <Users className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-green-800">Join 10,000+ grapplers improving their game</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3">
        <Button
          asChild
          variant="ghost"
          className="w-full"
        >
          <Link to="/auth/login">
            Return to Login
          </Link>
        </Button>
      </div>

      {/* Help Link */}
      <div className="text-center">
        <p className="text-sm text-gray-500">
          Still having trouble?{' '}
          <a 
            href="mailto:support@thematmonster.com" 
            className="text-primary hover:underline inline-flex items-center"
          >
            Contact support
            <ExternalLink className="ml-1 h-3 w-3" />
          </a>
        </p>
      </div>
    </div>
  );
}
